import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { database, ref, onValue } from "./firebase";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import BossNavigation from "./BossNavigation";
import confetti from "canvas-confetti";

export default function BossPage({ phone }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [rawStock, setRawStock] = useState([]);
  const [mostSold, setMostSold] = useState([]);
  const [monthlyTop10, setMonthlyTop10] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showBanner, setShowBanner] = useState(true);

  const today = new Date();
  const birthdayMap = {
    "7800700685": { day: 7, month: 6, name: "Mansoor" },           // July 7
    "9554736463": { day: 22, month: 11, name: "Sufiyan" },       // Dec 22
    "9628950249": { day: 27, month: 8, name: "Ali Jauhari" },   // Sept 27
  };

  const bossBirthday = birthdayMap[phone];
  const isBirthday =
    bossBirthday &&
    today.getDate() === bossBirthday.day &&
    today.getMonth() === bossBirthday.month;

  useEffect(() => {
    if (isBirthday) {
      confetti({ particleCount: 150, spread: 100 });
      const audio = new Audio("/birthday-music.mp3");
      audio.play().catch((e) => console.log("Audio failed", e));
    }
  }, [isBirthday]);

  useEffect(() => {
    if (phone) {
      const userRef = ref(database, `users/${phone}`);
      onValue(userRef, (snapshot) => {
        const data = snapshot.val();
        if (data?.name) {
          setUsername(data.name);
        }
      });
    }
  }, [phone]);

  useEffect(() => {
    const rawRef = ref(database, "rawStock");
    onValue(rawRef, (snapshot) => {
      const data = snapshot.val() || {};
      const items = Object.entries(data).map(([key, value]) => ({
        name: value.name,
        quantity: value.quantity,
      }));
      setRawStock(items);
    });
  }, []);

  function parseAnyDate(rawDate) {
    if (!rawDate || typeof rawDate !== "string") return null;
    const isoTry = new Date(rawDate);
    if (!isNaN(isoTry)) return isoTry;
    const parts = rawDate.split(/[\/\-]/);
    if (parts.length !== 3) return null;
    let day, month, year;
    if (parts[0].length === 4) {
      [year, month, day] = parts;
    } else {
      const first = parseInt(parts[0]);
      const second = parseInt(parts[1]);
      const third = parseInt(parts[2]);
      if (first > 12) {
        [day, month, year] = [first, second, third];
      } else if (second > 12) {
        [month, day, year] = [first, second, third];
      } else {
        [day, month, year] = [first, second, third];
      }
    }
    day = String(day).padStart(2, "0");
    month = String(month).padStart(2, "0");
    const final = new Date(`${year}-${month}-${day}`);
    return isNaN(final) ? null : final;
  }

  useEffect(() => {
    const invoiceRef = ref(database, "invoices");
    onValue(invoiceRef, (snapshot) => {
      const data = snapshot.val() || {};
      const productCount = {};
      const monthlyMap = {};

      const from = fromDate ? parseAnyDate(fromDate) : null;
      const to = toDate ? parseAnyDate(toDate) : null;

      Object.values(data).forEach((inv) => {
        const date = parseAnyDate(inv.date);
        if (!date) return;

        const inRange =
          (!from || date.getTime() >= from.getTime()) &&
          (!to || date.getTime() <= to.getTime());

        if (inRange) {
          inv.items?.forEach((item) => {
            productCount[item.product] =
              (productCount[item.product] || 0) + Number(item.quantity);
          });
        }

        const monthKey = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}`;

        if (!monthlyMap[monthKey]) monthlyMap[monthKey] = {};

        inv.items?.forEach((item) => {
          monthlyMap[monthKey][item.product] =
            (monthlyMap[monthKey][item.product] || 0) +
            Number(item.quantity);
        });
      });

      const chartData = Object.entries(productCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([product, quantity]) => ({ product, quantity }));

      setMostSold(chartData);

      const monthlyTop = Object.entries(monthlyMap).map(([monthKey, products]) => {
        const top10 = Object.entries(products)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10);
        return {
          month: monthKey,
          products: top10.map(([product, quantity]) => ({
            product,
            quantity,
          })),
        };
      });

      setMonthlyTop10(monthlyTop);
    });
  }, [fromDate, toDate]);

  const lowStock = rawStock.filter((item) => item.quantity < 10);

  return (
    <div className="container">
      <h2>👑 Welcome {username || phone}, the King of Jauhari</h2>

      {isBirthday && showBanner && (
        <div
          style={{
            backgroundColor: "#ffe4e1",
            padding: "20px",
            marginBottom: "20px",
            textAlign: "center",
            borderRadius: "15px",
            border: "2px solid #ff1493",
          }}
        >
          <h2 style={{ color: "#c2185b" }}>
            🎂 Happy Birthday to our Beloved {bossBirthday?.name} 🎉
          </h2>
          <p style={{ fontSize: "16px", color: "#333" }}>
            Dear {bossBirthday?.name}, may your year be filled with love,
            health, and success.
          </p>
        
        </div>
      )}

      <BossNavigation />

      {lowStock.length > 0 && (
        <div className="notification-banner">
          ⚠️ Low Raw Stock: {lowStock.map((s) => s.name).join(", ")}
        </div>
      )}

      <div className="section">
        <h4>📦 Raw Stock</h4>
        <table className="stock-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Quantity (kg/L)</th>
            </tr>
          </thead>
          <tbody>
            {rawStock.map((item, index) => (
              <tr key={index}>
                <td>{item.name}</td>
                <td>{item.quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="section">
        <h4>🔥 Best Selling Products (Top 10)</h4>
        <div style={{ marginBottom: "10px" }}>
          <label>
            From Date:{" "}
            <input
              type="date"
              onChange={(e) => {
                const date = new Date(e.target.value);
                const formatted = `${date.getFullYear()}/${String(
                  date.getMonth() + 1
                ).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}`;
                setFromDate(formatted);
              }}
            />
          </label>{" "}
          &nbsp;&nbsp;
          <label>
            To Date:{" "}
            <input
              type="date"
              onChange={(e) => {
                const date = new Date(e.target.value);
                const formatted = `${date.getFullYear()}/${String(
                  date.getMonth() + 1
                ).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}`;
                setToDate(formatted);
              }}
            />
          </label>
        </div>

        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={mostSold}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="product" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="quantity" fill="#83003c" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <table className="stock-table" style={{ marginTop: "20px" }}>
          <thead>
            <tr>
              <th>No</th>
              <th>Product</th>
              <th>Sold</th>
            </tr>
          </thead>
          <tbody>
            {mostSold.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.product}</td>
                <td>{item.quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="section">
        <h4>📅 Monthly Top 10 Products</h4>
        {monthlyTop10.map((month, idx) => (
          <div key={idx}>
            <h5>{month.month}</h5>
            <table className="stock-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Product</th>
                  <th>Sold</th>
                </tr>
              </thead>
              <tbody>
                {month.products.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.product}</td>
                    <td>{item.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
 